import React, { useState, useMemo } from "react";
import { AREAS, AREA_LINKS, type KnowledgeArea } from "@/lib/knowledge/areas";
import { KNOWLEDGE_PAGES, SIMULATION_PAGE } from "@/lib/knowledge/pages";
import { Link } from "@/components/common/Link";
import { ArrowRight, Sparkles, Network, ListFilter, CheckCircle, Clock, ExternalLink } from "lucide-react";
export function KnowledgeMap() {
  const [selectedId, setSelectedId] = useState<string>("philosophy");
  const [viewMode, setViewMode] = useState<"map" | "list">("map");
  const selectedArea = useMemo(() => AREAS.find(a => a.id === selectedId) || AREAS[1], [selectedId]);

  // Connections originating from or pointing to selected node
  const activeLinks = useMemo(() => {
    return AREA_LINKS.filter(l => l.from === selectedId || l.to === selectedId).map(link => {
      const isOutgoing = link.from === selectedId;
      const targetId = isOutgoing ? link.to : link.from;
      const targetArea = AREAS.find(a => a.id === targetId);
      return {
        ...link,
        isOutgoing,
        targetArea
      };
    });
  }, [selectedId]);

  // Set of connected area IDs for visual highlighting
  const connectedIdSet = useMemo(() => {
    const set = new Set<string>();
    activeLinks.forEach(l => {
      if (l.targetArea) set.add(l.targetArea.id);
    });
    return set;
  }, [activeLinks]);

  // Find linked page for open areas
  const openPageDef = useMemo(() => {
    if (selectedArea.status === "open" && selectedArea.pageId) {
      return KNOWLEDGE_PAGES.find(p => p.id === selectedArea.pageId);
    }
    return null;
  }, [selectedArea]);
  return <section id="knowledge-map" className="w-full scroll-mt-24 min-w-0">
      {/* Section Header with View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 pb-4 border-b border-border/50">
        <div>
          <span className="ql-eyebrow mb-2">SYSTEMIC CARTOGRAPHY</span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            The Knowledge Map
          </h2>
          <p className="font-sans text-sm sm:text-base text-muted-foreground mt-1 max-w-2xl">
            Inspect the 15 fundamental pillars of understanding. Select any domain node to reveal its underlying conceptual links and investigate its foundational curriculum.
          </p>
        </div>

        {/* View Switcher Toggle */}
        <div className="flex items-center gap-1.5 p-1 rounded-full border border-border bg-card/60 backdrop-blur-md self-start sm:self-auto shrink-0">
          <button type="button" onClick={() => setViewMode("map")} aria-pressed={viewMode === "map"} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-mono font-medium transition-colors min-h-[44px] ${viewMode === "map" ? "bg-primary text-primary-foreground font-semibold shadow-[0_0_12px_rgba(0,229,255,0.4)]" : "text-muted-foreground hover:text-foreground"}`}>
            <Network className="w-3.5 h-3.5" aria-hidden="true" />
            <span>Map View</span>
          </button>

          <button type="button" onClick={() => setViewMode("list")} aria-pressed={viewMode === "list"} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-mono font-medium transition-colors min-h-[44px] ${viewMode === "list" ? "bg-primary text-primary-foreground font-semibold shadow-[0_0_12px_rgba(0,229,255,0.4)]" : "text-muted-foreground hover:text-foreground"}`}>
            <ListFilter className="w-3.5 h-3.5" aria-hidden="true" />
            <span>Readable List</span>
          </button>
        </div>
      </div>

      {/* Map & Detail Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">
        {/* Left Column: Interactive Map Nodes or List */}
        <div className="lg:col-span-7 xl:col-span-8 min-w-0">
          {viewMode === "map" ? (/* Responsive Grid Map (SVG-free, accessible grid) */
        <div className="ql-glass p-4 sm:p-6 rounded-2xl border border-border relative min-w-0">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-border/40 text-xs font-mono text-muted-foreground">
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                  Select node to inspect connections
                </span>
                <span className="hidden sm:inline">
                  {AREAS.filter(a => a.status === "open").length} Labs Open / 15 Areas
                </span>
              </div>

              {/* Responsive Node Grid */}
              <div role="region" aria-label="Interactive Knowledge Nodes Grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 min-w-0">
                {AREAS.map(area => {
              const isSelected = area.id === selectedId;
              const isConnected = connectedIdSet.has(area.id);
              const isOpen = area.status === "open";
              return <button key={area.id} type="button" onClick={() => setSelectedId(area.id)} aria-pressed={isSelected} title={area.title} className={`aok-node-card text-left p-3.5 rounded-xl border relative flex flex-col justify-between min-h-[110px] min-w-0 group focus-visible:ring-2 focus-visible:ring-ring ${isSelected ? "aok-node-selected" : isConnected ? "aok-node-connected bg-card/60" : "border-border/60 bg-card/40 hover:bg-card/75"}`}>
                      <div className="flex items-start justify-between gap-1.5 w-full mb-1.5 min-w-0">
                        <span className="font-mono text-xs text-primary font-bold shrink-0">
                          {String(area.n).padStart(2, "0")}
                        </span>
                        {isOpen ? <span className="inline-flex items-center gap-1 font-mono text-[0.65rem] text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2 py-0.5 rounded-full shrink-0">
                            <CheckCircle className="w-2.5 h-2.5" />
                            Open
                          </span> : <span className="inline-flex items-center gap-1 font-mono text-[0.65rem] text-muted-foreground bg-muted/40 border border-border/50 px-2 py-0.5 rounded-full shrink-0">
                            <Clock className="w-2.5 h-2.5" />
                            Phase Two
                          </span>}
                      </div>

                      <h3 className="font-serif text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug break-words min-w-0">
                        {area.title}
                      </h3>

                      <p className="font-sans text-[0.75rem] text-muted-foreground line-clamp-2 mt-1 leading-relaxed break-words min-w-0">
                        {area.summary}
                      </p>
                    </button>;
            })}
              </div>

              {/* Map Legend */}
              <div className="mt-4 pt-3 border-t border-border/40 flex flex-wrap items-center gap-4 text-xs font-mono text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded border border-primary bg-primary/20" />
                  <span>Selected Node</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded border border-blue-400 bg-blue-500/20" />
                  <span>Connected Concept</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>Interactive Phase One Lab</span>
                </div>
              </div>
            </div>) : (/* Readable List Alternative */
        <div className="ql-glass p-4 sm:p-6 rounded-2xl border border-border min-w-0 space-y-3">
              <div className="text-xs font-mono text-muted-foreground mb-3 pb-2 border-b border-border/40">
                15 Core Domains of the Knowledge Curriculum
              </div>
              {AREAS.map(area => {
            const isSelected = area.id === selectedId;
            const isOpen = area.status === "open";
            return <button key={area.id} type="button" onClick={() => setSelectedId(area.id)} aria-pressed={isSelected} className={`w-full text-left p-4 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 min-w-0 ${isSelected ? "border-primary bg-primary/10 shadow-[0_0_16px_rgba(0,229,255,0.25)]" : "border-border/50 bg-card/30 hover:bg-card/70 hover:border-border"}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap min-w-0">
                        <span className="font-mono text-xs text-primary font-bold shrink-0">
                          Area {area.n}
                        </span>
                        {isOpen ? <span className="font-mono text-[0.65rem] text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2 py-0.5 rounded-full shrink-0">
                            Open Lab
                          </span> : <span className="font-mono text-[0.65rem] text-muted-foreground bg-muted/40 border border-border/50 px-2 py-0.5 rounded-full shrink-0">
                            Coming in Later Phase
                          </span>}
                      </div>
                      <h3 className="font-serif text-base font-bold text-foreground break-words min-w-0">
                        {area.title}
                      </h3>
                      <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed break-words min-w-0">
                        {area.summary}
                      </p>
                    </div>

                    <div className="shrink-0 flex items-center gap-2 text-xs font-mono text-primary font-medium">
                      <span>{isSelected ? "Inspecting" : "Select"}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </button>;
          })}
            </div>)}
        </div>

        {/* Right Column: Selected Node Detail & Connections Panel */}
        <div className="lg:col-span-5 xl:col-span-4 min-w-0">
          <div className="ql-glass p-5 sm:p-6 rounded-2xl border border-border aok-detail-panel sticky top-28 space-y-5 min-w-0">
            {/* Header info */}
            <div className="min-w-0">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap min-w-0">
                <span className="font-mono text-xs font-bold text-primary uppercase tracking-wider shrink-0">
                  DOMAIN {String(selectedArea.n).padStart(2, "0")}
                </span>
                {selectedArea.status === "open" ? <span className="inline-flex items-center gap-1 font-mono text-xs text-emerald-400 border border-emerald-500/30 bg-emerald-950/30 px-2.5 py-0.5 rounded-full shrink-0">
                    <CheckCircle className="w-3 h-3" />
                    Open Now
                  </span> : <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground border border-border/60 bg-muted/40 px-2.5 py-0.5 rounded-full shrink-0">
                    <Clock className="w-3 h-3" />
                    Coming in a Later Phase
                  </span>}
              </div>

              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground leading-snug break-words">
                {selectedArea.title}
              </h3>
            </div>

            {/* Summary & Character */}
            <div className="space-y-3 pt-2 border-t border-border/40">
              <div>
                <h4 className="font-mono text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Core Mandate
                </h4>
                <p className="font-sans text-sm text-foreground/90 leading-relaxed break-words">
                  {selectedArea.summary}
                </p>
              </div>

              <div>
                <h4 className="font-mono text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Epistemic Character
                </h4>
                <p className="font-sans text-xs text-muted-foreground leading-relaxed italic break-words">
                  {selectedArea.character}
                </p>
              </div>
            </div>

            {/* Page Entry Action / Simulation Link */}
            <div className="pt-2">
              {openPageDef ? <Link to={openPageDef.route} className="ql-neon-button w-full flex items-center justify-center gap-2 py-3 px-4 text-sm font-serif font-bold uppercase tracking-wider min-h-[44px]">
                  <span>ENTER {openPageDef.title.toUpperCase()}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link> : selectedArea.extraLink ? <div className="space-y-2">
                  <p className="font-sans text-xs text-muted-foreground">
                    While this master domain expands in Phase Two, its computational questions are directly modeled in our interactive simulation module.
                  </p>
                  <Link to={selectedArea.extraLink.route} className="ql-button-secondary w-full flex items-center justify-center gap-2 py-2.5 px-4 text-xs sm:text-sm font-serif font-semibold min-h-[44px]">
                    <ExternalLink className="w-3.5 h-3.5 text-primary" />
                    <span>{selectedArea.extraLink.title}</span>
                  </Link>
                </div> : <div className="p-3 rounded-lg border border-border/50 bg-card/30 text-xs font-mono text-muted-foreground">
                  Curriculum for this domain is scheduled for development in Phase Two. You can explore how it connects to active Phase One labs below.
                </div>}
            </div>

            {/* Interconnected Links list with explained `why` */}
            <div className="pt-3 border-t border-border/40">
              <div className="flex items-center gap-1.5 mb-3">
                <Network className="w-4 h-4 text-primary" />
                <h4 className="font-serif text-sm font-bold text-foreground uppercase tracking-wider">
                  Conceptual Links ({activeLinks.length})
                </h4>
              </div>

              {activeLinks.length > 0 ? <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1 min-w-0">
                  {activeLinks.map((link, idx) => <div key={idx} className="p-3 rounded-lg border border-border/50 bg-card/50 text-xs space-y-1.5 min-w-0">
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <span className="font-serif font-semibold text-primary break-words min-w-0 flex-1 leading-snug">
                          {link.targetArea?.title}
                        </span>
                        <button type="button" onClick={() => link.targetArea && setSelectedId(link.targetArea.id)} className="text-[0.7rem] font-mono text-muted-foreground hover:text-foreground underline underline-offset-2 min-h-[44px] -my-2 -mr-1 px-2.5 inline-flex items-center touch-manipulation focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded shrink-0">
                          Focus
                        </button>
                      </div>
                      <p className="font-sans text-muted-foreground leading-relaxed break-words min-w-0">
                        {link.why}
                      </p>
                    </div>)}
                </div> : <p className="font-sans text-xs text-muted-foreground">
                  No direct primary links mapped in current scope.
                </p>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}